import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useCart } from '../context/CartContext';
import PageHeader from '../components/PageHeader';
import api from '../api';

const Products = () => {
  const { settings } = useSettings();
  const { addToCart } = useCart();
  usePageTitle('Sản phẩm');
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const filter = searchParams.get('category') || 'all';
  const [sortBy, setSortBy] = useState('default');

  useEffect(() => {
    api.getCategories()
      .then(async (data) => {
        const parentMap = {};
        data.forEach(c => { parentMap[c.id] = c; });

        const parents = data.filter(c => !c.parentId);
        parents.sort((a, b) => {
          if ((a.sortOrder || 0) !== (b.sortOrder || 0)) {
            return (a.sortOrder || 0) - (b.sortOrder || 0);
          }
          return (a.name || '').localeCompare(b.name || '', 'vi');
        });

        const orderedList = [];
        parents.forEach(parent => {
          if (parent.showOnPage !== false) {
            orderedList.push({
              ...parent,
              thumbnail: parent.thumbnail || ""
            });
          }

          const children = data.filter(c => c.parentId === parent.id);
          children.sort((a, b) => {
            if ((a.sortOrder || 0) !== (b.sortOrder || 0)) {
              return (a.sortOrder || 0) - (b.sortOrder || 0);
            }
            return (a.name || '').localeCompare(b.name || '', 'vi');
          });

          children.forEach(child => {
            if (child.showOnPage !== false) {
              orderedList.push({
                ...child,
                thumbnail: child.thumbnail || parent.thumbnail || ""
              });
            }
          });
        });

        // Add any orphaned visible categories
        const addedIds = new Set(orderedList.map(c => c.id));
        data.forEach(c => {
          if (!addedIds.has(c.id) && c.showOnPage !== false) {
            orderedList.push({
              ...c,
              thumbnail: c.thumbnail || (c.parentId && parentMap[c.parentId] ? parentMap[c.parentId].thumbnail : "") || ""
            });
          }
        });

        const withProducts = [];
        for (const cat of orderedList) {
          try {
            const prods = await api.getProducts({ category: cat.slug });
            const items = Array.isArray(prods) ? prods : (prods.data || prods.items || []);
            if (items.length > 0) withProducts.push(cat);
          } catch (error) {}
        }

        setCategories([{ slug: 'all', name: 'Tất cả' }, ...withProducts]);
      })
      .catch(() => {
        setCategories([{ slug: 'all', name: 'Tất cả' }]);
      });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};

    if (filter !== 'all') params.category = filter;
    if (sortBy !== 'default') params.sort = sortBy;

    api.getProducts(params)
      .then((data) => {
        const items = Array.isArray(data) ? data : (data.data || data.items || []);
        setProducts(items);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [filter, sortBy]);

  // Filter state is derived directly from searchParams, no separate state sync is needed

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <i
        key={i}
        className={i < Math.round(rating) ? 'fas fa-star' : 'far fa-star'}
      ></i>
    ));
  };

  const formatPrice = (price) => {
    if (price == null || price === '' || Number(price) === 0) return 'Liên hệ';
    return new Intl.NumberFormat('vi-VN').format(price) + 'đ';
  };

  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      return api.imageUrl(product.images[0].url);
    }
    return 'https://via.placeholder.com/400x400?text=No+Image';
  };

  const getProductAlt = (product) => {
    if (product.images && product.images.length > 0 && product.images[0].altText) {
      return product.images[0].altText;
    }
    return product.name;
  };

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'newest':
        return b.id - a.id;
      case 'rating':
        return (b.averageRating || b.rating || 0) - (a.averageRating || a.rating || 0);
      default:
        return 0;
    }
  });

  const zaloLink = typeof settings?.zalo_url === 'string' ? settings.zalo_url : '#';

  return (
    <>
      <PageHeader
        title="Bộ sưu tập hoa tươi"
        subtitle="Khám phá những bó hoa tuyệt đẹp cho mọi dịp"
        breadcrumb={[
          { text: 'Trang chủ', link: '/' },
          { text: 'Sản phẩm' }
        ]}
      />

      <section className="products-section">
        <div className="container">
          <div className="products-filters">
            <div className="filter-group">
              <h3>Danh mục</h3>
              <div className="product-categories">
                {categories.map((category) => (
                  <button
                    key={category.slug}
                    className={`category-btn ${filter === category.slug ? 'active' : ''}`}
                    onClick={() => {
                      const newParams = new URLSearchParams(searchParams);
                      if (category.slug === 'all') {
                        newParams.delete('category');
                      } else {
                        newParams.set('category', category.slug);
                      }
                      setSearchParams(newParams);
                    }}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <h3>Sắp xếp theo</h3>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="default">Mặc định</option>
                <option value="price-low">Giá thấp</option>
                <option value="price-high">Giá cao</option>
                <option value="newest">Mới nhất</option>
                <option value="rating">Đánh giá cao</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="products-empty">Đang tải sản phẩm...</div>
          ) : sortedProducts.length === 0 ? (
            <div className="products-empty">Không có sản phẩm nào.</div>
          ) : (
            <div className="products-grid">
              {sortedProducts.map((product) => {
                const rating = Number(product.averageRating ?? product.rating ?? 0);
                const count = Number(product.reviewCount || 0);
                const productLink = product.slug
                  ? `/san-pham/${product.slug}`
                  : `/products/${product.id}`;

                return (
                  <div key={product.id} className="product-card">
                    <Link to={productLink} className="product-image">
                      <img
                        src={getProductImage(product)}
                        alt={getProductAlt(product)}
                        loading="lazy"
                      />
                    </Link>

                    <div className="product-info">
                      <div className="product-name-row">
                        <h3 className="product-name">
                          <Link to={productLink} className="product-title-link">
                            {product.name}
                          </Link>
                        </h3>
                        <button 
                          className="btn-add-to-cart-quick" 
                          onClick={(e) => {
                            e.preventDefault();
                            addToCart(product);
                          }}
                          title="Thêm vào giỏ hàng"
                        >
                          <i className="fas fa-shopping-cart"></i>
                        </button>
                      </div>

                      <div className="product-rating">
                        {renderStars(rating)}

                        {count > 0 && (
                          <>
                            <span className="rating-number">{rating.toFixed(1)}/5</span>
                            <span className="rating-count">({count} đánh giá)</span>
                          </>
                        )}
                      </div>

                      <div className="product-price">
                        {formatPrice(product.price)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Products;